import React, { useEffect, useRef } from "react";

const SpeechToText = ({
  isListening,
  onStopListening,
  setTranscript,
  setUserInput,
  setMicError,
  language = "en-US",
}) => {
  const recognitionRef = useRef(null);
  const streamRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const animationFrameRef = useRef(null);

  const speechStartedRef = useRef(false);
  const silenceStartRef = useRef(null);
  const stoppingRef = useRef(false);

  const finalTranscriptRef = useRef("");
  const interimTranscriptRef = useRef("");

  const SILENCE_TIME = 1500;
  const SILENCE_THRESHOLD = 0.02;

  const cleanup = () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(
        animationFrameRef.current
      );

      animationFrameRef.current = null;
    }

    if (audioContextRef.current) {
      try {
        audioContextRef.current.close();
      } catch (error) {
        console.error(error);
      }

      audioContextRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current
        .getTracks()
        .forEach((track) => {
          track.stop();
        });

      streamRef.current = null;
    }

    analyserRef.current = null;
  };

  const stopRecognition = () => {
    if (stoppingRef.current) {
      return;
    }

    stoppingRef.current = true;

    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (error) {
        console.error(error);
      }
    }

    cleanup();

    onStopListening();
  };

  const getVolume = () => {
    const analyser = analyserRef.current;

    if (!analyser) {
      return 0;
    }

    const bufferLength =
      analyser.fftSize;

    const dataArray =
      new Uint8Array(bufferLength);

    analyser.getByteTimeDomainData(
      dataArray
    );

    let sum = 0;

    for (
      let i = 0;
      i < bufferLength;
      i++
    ) {
      const value =
        (dataArray[i] - 128) / 128;

      sum += value * value;
    }

    return Math.sqrt(
      sum / bufferLength
    );
  };

  const checkSilence = () => {
    if (!isListening) {
      return;
    }

    if (stoppingRef.current) {
      return;
    }

    const volume = getVolume();

    const now = Date.now();

    if (
      volume > SILENCE_THRESHOLD
    ) {
      speechStartedRef.current = true;
      silenceStartRef.current = null;
    } else if (
      speechStartedRef.current
    ) {
      if (!silenceStartRef.current) {
        silenceStartRef.current = now;
      }

      const silentFor =
        now - silenceStartRef.current;

      if (
        silentFor >= SILENCE_TIME
      ) {
        stopRecognition();
        return;
      }
    }

    animationFrameRef.current =
      requestAnimationFrame(
        checkSilence
      );
  };

  const startMicrophone = async () => {
    try {
      const stream =
        await navigator.mediaDevices.getUserMedia(
          {
            audio: {
              channelCount: 1,
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
            },
          }
        );

      streamRef.current = stream;

      const AudioContext =
        window.AudioContext ||
        window.webkitAudioContext;

      if (!AudioContext) {
        setMicError(
          "Audio processing is not supported in this browser."
        );
        return;
      }

      const audioContext =
        new AudioContext();

      audioContextRef.current =
        audioContext;

      const analyser =
        audioContext.createAnalyser();

      analyser.fftSize = 2048;
      analyser.smoothingTimeConstant = 0.3;

      analyserRef.current =
        analyser;

      const source =
        audioContext.createMediaStreamSource(
          stream
        );

      source.connect(analyser);

      speechStartedRef.current =
        false;

      silenceStartRef.current =
        null;

      animationFrameRef.current =
        requestAnimationFrame(
          checkSilence
        );
    } catch (error) {
      console.error(
        "Microphone error:",
        error
      );

      if (
        error.name ===
        "NotAllowedError"
      ) {
        setMicError(
          "Microphone permission denied."
        );
      } else if (
        error.name ===
        "NotFoundError"
      ) {
        setMicError(
          "No microphone found."
        );
      } else {
        setMicError(
          "Could not access microphone."
        );
      }

      onStopListening();
    }
  };

  const startSpeechRecognition =
    () => {
      const SpeechRecognition =
        window.SpeechRecognition ||
        window.webkitSpeechRecognition;

      if (!SpeechRecognition) {
        setMicError(
          "Speech recognition is not supported. Please use Google Chrome."
        );

        onStopListening();
        return;
      }

      const recognition =
        new SpeechRecognition();

      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = language;

      recognitionRef.current =
        recognition;

      finalTranscriptRef.current =
        "";

      interimTranscriptRef.current =
        "";

      recognition.onstart = () => {
        setMicError("");
      };

      recognition.onresult = (
        event
      ) => {
        let finalText = "";
        let interimText = "";

        for (
          let i = event.resultIndex;
          i < event.results.length;
          i++
        ) {
          const text =
            event.results[i][0]
              .transcript;

          if (
            event.results[i].isFinal
          ) {
            finalText += text;
          } else {
            interimText += text;
          }
        }

        if (finalText) {
          finalTranscriptRef.current +=
            finalText;
        }

        interimTranscriptRef.current =
          interimText;

        const completeText = (
          finalTranscriptRef.current +
          interimTranscriptRef.current
        ).trim();

        if (completeText) {
          setTranscript(
            completeText
          );

          setUserInput(
            completeText
          );
        }
      };

      recognition.onerror = (
        event
      ) => {
        console.error(
          "Speech recognition error:",
          event.error
        );

        if (
          event.error ===
          "not-allowed"
        ) {
          setMicError(
            "Microphone permission denied."
          );
        } else if (
          event.error ===
          "audio-capture"
        ) {
          setMicError(
            "No microphone was found."
          );
        } else if (
          event.error ===
          "network"
        ) {
          setMicError(
            "Speech recognition requires an internet connection."
          );
        }
      };

      recognition.onend = () => {
        if (
          !stoppingRef.current &&
          isListening
        ) {
          try {
            recognition.start();
          } catch (error) {
            console.error(
              "Recognition restart error:",
              error
            );
          }
        }
      };

      try {
        recognition.start();
      } catch (error) {
        console.error(
          "Recognition start error:",
          error
        );
      }
    };

  useEffect(() => {
    if (!isListening) {
      stoppingRef.current = true;

      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (error) {
          console.error(error);
        }
      }

      cleanup();

      return;
    }

    stoppingRef.current = false;

    startMicrophone();
    startSpeechRecognition();

    return () => {
      stoppingRef.current = true;

      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (error) {
          console.error(error);
        }
      }

      cleanup();
    };
  }, [isListening, language]);

  return null;
};

export default SpeechToText;